import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CalendarClock, ClipboardList, PackageCheck, ShoppingCart, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { OrderStatusBadge, shortDate } from '@/components/manufacturing/planning/shared';
import { fmtQty } from '@/features/manufacturing/planning/engine';
import type { VendorOrderGroup } from '@/features/manufacturing/planning/procurementCalendar';
import type { PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { GROUP_SEVERITY_SURFACE, GROUP_SEVERITY_ICON } from './shared';

interface VendorOrderCardProps {
  group: VendorOrderGroup;
  model: PlanningModel;
  canCreate: boolean;
  onCreatePo: (group: VendorOrderGroup) => void;
  onOpenItem: (itemId: string) => void;
  className?: string;
}

/**
 * One vendor's order for one day: the lines the planner wants on it,
 * when it has to go out, what it costs, and the button that drafts it.
 */
export function VendorOrderCard({ group, model, canCreate, onCreatePo, onOpenItem, className }: VendorOrderCardProps) {
  const { today } = model;
  const deadline = group.latestDate;
  const deadlinePassed = group.status === 'late';
  const deadlineToday = !deadlinePassed && deadline != null && deadline <= today;
  const firstItemId = group.orders[0]?.itemId;

  return (
    <div className={cn('overflow-hidden rounded-xl border bg-card', GROUP_SEVERITY_SURFACE[group.severity], className)}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={cn(
              'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border',
              GROUP_SEVERITY_ICON[group.severity],
            )}
          >
            {group.unassignable ? <AlertTriangle className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate font-medium">{group.vendorName}</h3>
              <OrderStatusBadge status={group.status} />
              <Badge variant="muted">
                {group.orders.length} {group.orders.length === 1 ? 'part' : 'parts'}
              </Badge>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {deadline && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1',
                    (deadlinePassed || deadlineToday) && 'font-medium text-fresh-salmon-dark',
                  )}
                >
                  <CalendarClock className="h-3 w-3" />
                  {deadlinePassed ? 'Past the last safe day' : deadlineToday ? 'Last safe day is today' : `Last safe day ${shortDate(deadline)}`}
                </span>
              )}
              {group.expectedArrival && (
                <span className="inline-flex items-center gap-1">
                  <PackageCheck className="h-3 w-3" />
                  Lands ~{shortDate(group.expectedArrival)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium tabular-nums">{formatCurrency(group.totalCents)}</div>
          {group.linesWithoutCost > 0 && (
            <div className="text-xs text-fresh-salmon-dark">
              {group.linesWithoutCost} {group.linesWithoutCost === 1 ? 'line has' : 'lines have'} no cost
            </div>
          )}
        </div>
      </div>

      <ul className="divide-y divide-border/70 border-t border-border/70 bg-card">
        {group.orders.map((order) => {
          const plan = model.itemsById.get(order.itemId);
          const pb = order.priceBreakOption;
          return (
            <li key={order.id} className="flex items-start gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-mono text-[11px] text-muted-foreground">{plan?.item.sku ?? order.itemId}</span>
                  <span className="truncate text-sm font-medium">{plan?.item.name ?? 'Part'}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span>needed {shortDate(order.needByDate)}</span>
                  {order.status !== group.status && <OrderStatusBadge status={order.status} />}
                  {order.quantity !== order.netRequirement && (
                    <span>
                      need {fmtQty(order.netRequirement)}
                      {order.moqApplied && ' · MOQ'}
                      {order.multipleApplied && ' · pack size'}
                    </span>
                  )}
                  {pb?.recommended && (
                    <span className="inline-flex items-center gap-1 text-bright-moss-dark">
                      <Sparkles className="h-3 w-3" />
                      {fmtQty(pb.quantity)} @ {formatCurrency(pb.unitCostCents)} is cheaper in total
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => onOpenItem(order.itemId)}
                    className="inline-flex items-center gap-0.5 text-info hover:underline"
                  >
                    Part details <ArrowUpRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <div className="shrink-0 text-right tabular-nums">
                <div className="text-sm font-medium">
                  {fmtQty(order.quantity)}
                  {plan && plan.item.baseUom !== 'each' && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">{plan.item.baseUom}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {order.totalCents == null ? <span className="text-fresh-salmon-dark">no cost</span> : formatCurrency(order.totalCents)}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/70 bg-secondary/40 px-4 py-2.5">
        {group.unassignable ? (
          <span className="text-xs text-fresh-salmon-dark">
            No supplier on file, so nothing can be drafted.{' '}
            {firstItemId && (
              <Link to={`/catalog/items/${firstItemId}?tab=suppliers`} className="font-medium underline-offset-2 hover:underline">
                Add a supplier
              </Link>
            )}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            One draft PO for {group.vendorName}
            {group.latestDate ? `, to place by ${shortDate(group.latestDate)}` : ''}.
          </span>
        )}
        {!group.unassignable &&
          (canCreate ? (
            <Button type="button" size="sm" onClick={() => onCreatePo(group)}>
              <ClipboardList className="h-3.5 w-3.5" />
              Create draft PO
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">Only admins can create POs.</span>
          ))}
      </div>
    </div>
  );
}
